/**
 * One-time offers on Trips in the iOS app: notifications first, then the
 * Face ID lock, one card per visit. iOS shows its notification prompt only
 * once, so the app asks here, where it can say what the notifications are
 * for, rather than at launch. "Not now" is remembered on this phone until
 * sign-out, and both stay in Profile → This iPhone (NativeAppSettingsCard).
 *
 * App only.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Bell, ScanFace, Fingerprint } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { REWARDS_SECTION_CARD } from '@/constants/brandColors';
import { isNativeApp } from '@/native/bootstrap';
import { enablePushNotifications, hasBeenAskedAboutPush, markAskedAboutPush, pushPermission } from '@/native/push';
import { biometricAvailability, faceIdLockEnabled, faceIdOffered, markFaceIdOffered, setFaceIdLock, verifyOwner } from '@/native/faceId';

/** Which offer to show, if any: 'push', 'faceId' or null. */
export async function pickTripsPrompt({ permission, askedAboutPush, lockOn, offeredLock, availability }) {
  if (permission === 'prompt' && !askedAboutPush) return 'push';
  if (lockOn || offeredLock) return null;
  const bio = await availability();
  return bio.available && bio.kind !== 'passcode' ? 'faceId' : null;
}

export function PromptCard({ icon: Icon, title, body, done, working, onYes, onNo }) {
  return (
    <div className={`${REWARDS_SECTION_CARD} mb-5`}>
      <div className="flex items-start gap-3.5 px-5 py-4">
        <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-[hsl(228,35%,58%)]/20 bg-[hsl(228,40%,38%)]/[0.16]">
          <Icon className="h-4 w-4 text-[hsl(228,30%,72%)]" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-medium tracking-tight text-white">{title}</h3>
          <p className="mt-1 text-[13px] font-normal leading-relaxed text-white/60">{done || body}</p>
          {!done && (
            <div className="mt-3 flex items-center gap-2">
              <Button type="button" onClick={onYes} disabled={working} className="h-9 rounded-full bg-white px-4 text-[13px] font-medium text-black hover:bg-white/90">
                Turn on
              </Button>
              <Button type="button" variant="ghost" onClick={onNo} disabled={working} className="h-9 rounded-full px-4 text-[13px] font-normal text-white/60 hover:bg-white/10 hover:text-white">
                Not now
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

PromptCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  done: PropTypes.string,
  working: PropTypes.bool,
  onYes: PropTypes.func.isRequired,
  onNo: PropTypes.func.isRequired,
};

export default function NativeTripsPrompt() {
  const [card, setCard] = useState(null);
  const [kind, setKind] = useState('Face ID');
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!isNativeApp) return undefined;
    let alive = true;
    (async () => {
      const permission = await pushPermission();
      const pick = await pickTripsPrompt({
        permission,
        askedAboutPush: hasBeenAskedAboutPush(),
        lockOn: faceIdLockEnabled(),
        offeredLock: faceIdOffered(),
        availability: async () => {
          const bio = await biometricAvailability();
          if (bio.kind) setKind(bio.kind);
          return bio;
        },
      });
      if (alive) setCard(pick);
    })();
    return () => { alive = false; };
  }, []);

  if (!card) return null;
  const finish = (message) => {
    setDone(message);
    setTimeout(() => setCard(null), 4000);
  };

  if (card === 'push') {
    return (
      <PromptCard
        icon={Bell}
        title="Get trip updates on this iPhone"
        body="We'll let you know when a payment goes through or needs attention, when your flight changes, and other news about your trips."
        done={done}
        working={working}
        onYes={async () => {
          setWorking(true);
          const result = await enablePushNotifications();
          setWorking(false);
          if (result === 'enabled') finish('Notifications are on. You can turn them off in Profile.');
          else if (result === 'denied') setCard(null);
          else finish("Notifications couldn't be turned on just now. You can try again in Profile.");
        }}
        onNo={() => { markAskedAboutPush(); setCard(null); }}
      />
    );
  }

  return (
    <PromptCard
      icon={kind === 'Touch ID' ? Fingerprint : ScanFace}
      title={`Lock Defera with ${kind}`}
      body={`Ask for ${kind} when Defera opens, so only you can see your trips and payments.`}
      done={done}
      working={working}
      onYes={async () => {
        setWorking(true);
        const ok = await verifyOwner();
        setWorking(false);
        if (!ok) return;
        setFaceIdLock(true);
        markFaceIdOffered();
        finish(`${kind} lock is on. You can turn it off in Profile.`);
      }}
      onNo={() => { markFaceIdOffered(); setCard(null); }}
    />
  );
}
